import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Paper, CircularProgress, Button, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useNavigate } from 'react-router-dom';
import { Pie, Doughnut } from 'react-chartjs-2';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContextNavBar from '../components/ContextNavBar';
import 'chart.js/auto';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import api from '../axios';
Chart.register(ArcElement, Tooltip, Legend);


function DashboardErrorBoundary({ children }) {
  // Simples boundary para evitar crash
  return children;
}

function Dashboard() {
  const navigate = useNavigate();
  const [dados, setDados] = useState({});
  const [loading, setLoading] = useState(false);

  // Verificação global: logar qualquer valor React element nos dados
  function deepCheckForReactElement(obj, path = '') {
    if (typeof obj === 'object' && obj !== null) {
      if (obj.$$typeof) {
        console.error('DASHBOARD: React element encontrado em', path, obj);
        return true;
      }
      for (const k in obj) {
        if (deepCheckForReactElement(obj[k], path ? `${path}.${k}` : k)) return true;
      }
    }
    return false;
  }
  deepCheckForReactElement(dados, 'dados');

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      const endpoints = [
        ['alunosPorSexo', '/dashboard/alunos-por-sexo'],
        ['alunosPorEstadoCivil', '/dashboard/alunos-por-estado-civil'],
        ['alunosPorProfessor', '/dashboard/alunos-por-professor'],
        ['alunosPorTurma', '/dashboard/alunos-por-turma'],
        ['alunosPorMotivo', '/dashboard/alunos-por-motivo'],
        ['alunosPresentes', '/dashboard/alunos-presentes'],
        ['alunosMaisFaltas', '/dashboard/alunos-mais-faltas'],
        ['alunosFormadosMes', '/dashboard/alunos-formados-mes'],
        ['alunosFormadosTotal', '/dashboard/alunos-formados'],
      ];
      const results = {};
      for (const [key, url] of endpoints) {
        try {
          const res = await api.get(url);
          results[key] = res.data;
        } catch (e) {
          // Valores padrão para cada tipo de dado
          if (key === 'alunosMaisFaltas') results[key] = [];
          else if (key === 'alunosPresentes') results[key] = { presentes: 0 };
          else if (key === 'alunosFormadosMes') results[key] = { formados_mes: 0 };
          else if (key === 'alunosFormadosTotal') results[key] = { formados: 0 };
          else results[key] = {};
        }
      }
      setDados(results);
      setLoading(false);
    }
    fetchAll();
  }, []);

  if (loading) return <Container sx={{ textAlign: 'center', mt: 8 }}><CircularProgress /></Container>;

  // Debug: logar todos os dados recebidos
  console.log('DADOS DASHBOARD:', dados);
  // Log detalhado de todos os campos
  Object.entries(dados).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        if (typeof item === 'object' && item !== null) {
          Object.entries(item).forEach(([k, v]) => {
            console.log(`DADOS DASHBOARD: ${key}[${idx}].${k} =`, v);
          });
        } else {
          console.log(`DADOS DASHBOARD: ${key}[${idx}] =`, item);
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([k, v]) => {
        console.log(`DADOS DASHBOARD: ${key}.${k} =`, v);
      });
    } else {
      console.log(`DADOS DASHBOARD: ${key} =`, value);
    }
  });

  // Função utilitária para checar se objeto é válido e não vazio
  const isValidObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length > 0;

  // Função utilitária única para garantir que só string/number seja renderizado
  function safeText(value, label = '') {
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null && value.$$typeof) {
      console.error('DASHBOARD: Valor é um React element', label, value);
      return '[componente React]';
    }
    if (Array.isArray(value)) {
      console.warn('DASHBOARD: Valor array', label, value);
      return '[array]';
    }
    if (typeof value === 'object' && value !== null) {
      console.warn('DASHBOARD: Valor objeto', label, value, Object.keys(value));
      return '[objeto]';
    }
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (value === null || value === undefined) return '';
    // Qualquer outro tipo
    return String(value);
  }

  function safeObjectKeys(obj, label = '') {
    if (!isValidObject(obj)) return [];
    return Object.keys(obj).map((k, idx) => safeText(k, `${label}.key[${idx}]`));
  }

  function safeObjectValues(obj, label = '') {
    if (!isValidObject(obj)) return [];
    return Object.values(obj).map((v, idx) => safeText(v, `${label}.val[${idx}]`));
  }

  // Sanitização extra para garantir que labels/data nunca sejam objetos React
  let pieLabels = safeObjectKeys(dados.alunosPorSexo, 'alunosPorSexo').map(l => safeText(l, 'alunosPorSexo.label'));
  let pieData = safeObjectValues(dados.alunosPorSexo, 'alunosPorSexo').map(v => typeof v === 'number' ? v : 0);
  pieLabels = pieLabels.filter(l => typeof l === 'string' || typeof l === 'number');
  pieData = pieData.filter(v => typeof v === 'number');
  const pieChartData = {
    labels: pieLabels,
    datasets: [{
      data: pieData,
      backgroundColor: ['#1976d2', '#e91e63'],
    }],
  };
  // Logar objeto inteiro do Pie
  console.log('DASHBOARD PIE data prop:', pieChartData);
  // Checagem extra: nenhum valor pode ser objeto React
  if (pieChartData.labels.some(l => typeof l === 'object' && l !== null && l.$$typeof)) {
    console.error('DASHBOARD PIE: Label é React element!', pieChartData.labels);
  }
  if (pieChartData.datasets.some(ds => ds.data.some(v => typeof v !== 'number'))) {
    console.error('DASHBOARD PIE: Data contém valor não numérico!', pieChartData.datasets);
  }
  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };
  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <DashboardErrorBoundary>
        <Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, md: 2 }, maxWidth: 900 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
          onClick={() => navigate('/')}
        >
          Voltar para página principal
        </Button>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.3rem', md: '2rem' } }}>Dashboard</Typography>
  <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Alunos por Sexo */}
          <Grid item xs={12} sm={6} md={6}>
            <Paper sx={{ p: 3, height: 340, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PeopleOutlineIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6">Alunos por Sexo</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Distribuição dos alunos por sexo (masculino/feminino) na instituição.
              </Typography>
                {isValidObject(dados.alunosPorSexo) ? (
                  <div style={{ height: 180, position: 'relative' }}>
                    <Pie
                      data={pieChartData}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
                )}
            </Paper>
          </Grid>

          {/* Alunos por Estado Civil */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FavoriteBorderIcon sx={{ color: '#e91e63', fontSize: 28 }} />
                <Typography variant="h6">Alunos por Estado Civil</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Visualização do perfil de estado civil dos alunos cadastrados.
              </Typography>
                {isValidObject(dados.alunosPorEstadoCivil) ? (
                  <div style={{ height: 180, position: 'relative' }}>
                    <Pie
                      data={{
                        labels: safeObjectKeys(dados.alunosPorEstadoCivil, 'alunosPorEstadoCivil'),
                        datasets: [{
                          data: safeObjectValues(dados.alunosPorEstadoCivil, 'alunosPorEstadoCivil').map(v => typeof v === 'number' ? v : 0),
                          backgroundColor: ['#1976d2', '#e91e63', '#ff9800', '#4caf50'],
                        }],
                      }}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
                )}
            </Paper>
          </Grid>

          {/* Alunos por Professor */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolOutlinedIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                <Typography variant="h6">Alunos por Professor</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quantidade de alunos por professor responsável.
              </Typography>
                {isValidObject(dados.alunosPorProfessor) ? (
                  <div style={{ height: 180, position: 'relative' }}>
                    <Pie
                      data={{
                        labels: safeObjectKeys(dados.alunosPorProfessor, 'alunosPorProfessor'),
                        datasets: [{
                          data: safeObjectValues(dados.alunosPorProfessor, 'alunosPorProfessor').map(v => typeof v === 'number' ? v : 0),
                          backgroundColor: ['#1976d2', '#e91e63', '#ff9800', '#4caf50', '#9c27b0'],
                        }],
                      }}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
                )}
            </Paper>
          </Grid>

          {/* Alunos por Turma */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <GroupsOutlinedIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                <Typography variant="h6">Alunos por Turma</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Distribuição dos alunos por turma cadastrada.
              </Typography>
                {isValidObject(dados.alunosPorTurma) ? (
                  <div style={{ height: 180, position: 'relative' }}>
                    <Pie
                      data={{
                        labels: safeObjectKeys(dados.alunosPorTurma, 'alunosPorTurma'),
                        datasets: [{
                          data: safeObjectValues(dados.alunosPorTurma, 'alunosPorTurma').map(v => typeof v === 'number' ? v : 0),
                          backgroundColor: ['#1976d2', '#e91e63', '#ff9800', '#4caf50', '#9c27b0', '#00bcd4'],
                        }],
                      }}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
                )}
            </Paper>
          </Grid>

          {/* Alunos por Motivo */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationCityOutlinedIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
                <Typography variant="h6">Alunos por Motivo</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Motivo de entrada dos alunos (batismo, transferência, etc).
              </Typography>
                {isValidObject(dados.alunosPorMotivo) ? (
                  <div style={{ height: 180, position: 'relative' }}>
                    <Pie
                      data={{
                        labels: safeObjectKeys(dados.alunosPorMotivo, 'alunosPorMotivo'),
                        datasets: [{
                          data: safeObjectValues(dados.alunosPorMotivo, 'alunosPorMotivo').map(v => typeof v === 'number' ? v : 0),
                          backgroundColor: ['#1976d2', '#e91e63', '#ff9800', '#4caf50', '#9c27b0', '#00bcd4', '#cddc39'],
                        }],
                      }}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
                )}
            </Paper>
          </Grid>

          {/* Indicadores simples */}

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                <Typography variant="h6">Alunos Presentes</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total de alunos presentes no último evento registrado.
              </Typography>
              <Typography variant="h3" color="primary">
                {safeText(dados.alunosPresentes?.presentes, 'alunosPresentes.presentes')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmojiEventsOutlinedIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                <Typography variant="h6">Alunos Formados (Total)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quantidade de alunos que concluíram 8 lições únicas (não repetidas).
              </Typography>
              <Typography variant="h3" color="primary">
                {safeText(dados.alunosFormadosTotal?.formados, 'alunosFormadosTotal.formados')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarTodayOutlinedIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6">Alunos Formados no Trimestre</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Todos os alunos que completaram as 8 lições únicas entram nessa conta.
              </Typography>
              <Typography variant="h3" color="primary">
                {safeText(dados.alunosFormadosTotal?.formados, 'alunosFormadosTotal.formados')}
              </Typography>
            </Paper>
          </Grid>

          {/* Alunos com mais faltas */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReportProblemOutlinedIcon sx={{ color: '#f44336', fontSize: 28 }} />
                <Typography variant="h6">Alunos com Mais Faltas</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Lista dos alunos que mais faltaram nos eventos recentes.
              </Typography>
              {Array.isArray(dados.alunosMaisFaltas) && dados.alunosMaisFaltas.length > 0 ? (
                dados.alunosMaisFaltas.map((aluno, idx) => (
                  <Typography key={idx} variant="body1">
                    {safeText(aluno.nome, `alunosMaisFaltas[${idx}].nome`)} - Faltas: {safeText(aluno.faltas, `alunosMaisFaltas[${idx}].faltas`)}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Sem dados para exibir.</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
        </Container>
      </DashboardErrorBoundary>
      <Footer />
    </>
  );

}

export default Dashboard;